import { useEffect, useState } from "react";
import { Bell } from "lucide-react";
import { api } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { formatDateTime } from "../lib/dateUtils";

const TYPE_LABELS = {
  system_notification: "System",
  admin_message: "Admin",
  photographer_message: "Message",
  session_announcement: "Announcement",
  matching_notification: "Match",
  cancellation: "Cancelled",
  reminder: "Reminder",
  gallery_ready: "Gallery",
};

/** Bell + dropdown inbox shared across the public Header (Stranger/Photographer)
 * and the AdminTopBar. Polls unread count every 30s; opening the popover marks
 * visible items read. */
export const NotificationBell = ({ dark = false }) => {
  const [notifications, setNotifications] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  const loadCount = () => api.get("/notifications/unread-count").then(({ data }) => setUnreadCount(data.unread_count)).catch(() => {});

  useEffect(() => {
    loadCount();
    const interval = setInterval(loadCount, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpen = async (v) => {
    setOpen(v);
    if (v) {
      const { data } = await api.get("/notifications/mine");
      setNotifications(data.slice(0, 10));
      const unread = data.filter((n) => !n.read);
      if (unread.length) {
        await api.put("/notifications/read-all");
        setUnreadCount(0);
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell-button"
          aria-label="Notifications"
          className={`relative w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${
            dark ? "border-white/20 hover:border-stranger-orange" : "border-soft-gray hover:border-stranger-orange"
          }`}
        >
          <Bell className={`w-4 h-4 ${dark ? "text-clean-white/80" : "text-midnight-black/70"}`} />
          {unreadCount > 0 && (
            <span data-testid="notification-unread-badge" className="absolute -top-1 -right-1 bg-stranger-orange text-clean-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 max-h-96 overflow-y-auto" data-testid="notification-popover">
        <p className="text-xs font-bold uppercase tracking-wide mb-2">Notifications</p>
        {notifications === null ? (
          <p className="text-sm text-midnight-black/60">Loading...</p>
        ) : notifications.length === 0 ? (
          <p className="text-sm text-midnight-black/60" data-testid="no-notifications">No notifications yet.</p>
        ) : (
          <div className="space-y-2" data-testid="notification-list">
            {notifications.map((n) => (
              <div key={n.id} className="border-b border-soft-gray last:border-0 pb-2 last:pb-0" data-testid={`notification-item-${n.id}`}>
                <p className="text-[9px] font-bold uppercase tracking-wide text-stranger-orange">{TYPE_LABELS[n.message_type] || n.message_type}</p>
                <p className="text-sm font-bold">{n.subject}</p>
                <p className="text-xs text-midnight-black/60">{n.content}</p>
                <p className="text-[10px] text-midnight-black/40 mt-1">{formatDateTime(n.created_at)}</p>
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
